/**
 * Shop stock, taken from the café's own catalogue.
 *
 * **This file is the data contract.** The client confirmed 2026-09-12 that
 * products and pricing will be managed externally, so everything the shop
 * renders is derived from the `Product` records below and nothing else.
 * The properties that make that swap safe, which should be preserved by
 * whatever ends up feeding this:
 *
 * - `published` and `sold` are separate. `published: false` removes an item
 *   from the site entirely; `sold: true` keeps its page alive and says the
 *   piece has gone, so a shared or bookmarked link is not a dead end (D17).
 * - `stockQuantity` and `oneOff` drive the scarcity wording. No component
 *   hard-codes "one of one" or a stock count.
 * - `photoPending` is what makes a stand-in photograph declare itself. A
 *   feed that omits it will silently present the wrong picture as the
 *   product (D22).
 * - `price` is a NUMBER in pounds, unlike the menu's printed-price strings,
 *   because the shop formats and compares it.
 * - `id` is internal only and must never render (D36).
 *
 * Prices, stock counts and the descriptions of the reclaimed pieces are
 * illustrative until the real catalogue lands. See I07.
 */

import { IMAGES, PRODUCT_PLACEHOLDER } from './images'

export type ProductStatusLabel =
  | 'One of One'
  | 'Handmade'
  | 'Limited Stock'
  | 'Low On Fuel Merchandise'

export interface Product {
  /**
   * Internal only — a stable React key and nothing more. It is NOT shown
   * anywhere and must not be: stock is managed outside this site, so a
   * code printed on a page would be ours alone and would match nothing
   * the café or the customer holds. Registry D36.
   */
  id: string
  slug: string
  name: string
  image: string
  imageAlt: string
  /**
   * True when `image` is the shared merchandise photograph standing in for
   * a piece that has not been photographed yet. The card and the page both
   * say so, because a rail of t-shirts is not a piston clock and nobody
   * should have to work that out for themselves. Registry D22.
   */
  photoPending?: boolean
  supportingImages?: { src: string; alt: string }[]
  /** Pounds sterling. */
  price: number
  shortDescription: string
  /** Provenance, only where it is genuinely known. */
  history?: string
  stockQuantity: number
  oneOff: boolean
  sold: boolean
  /**
   * Only ever a maker we can actually name. The reclaimed one-offs had
   * plausible invented makers ("Derbyshire Reclaim Works", "Peak Leather
   * Co.") and those are now REMOVED rather than flagged as placeholders:
   * an invented company name is very likely to be a real company, and
   * attributing goods to the wrong maker is a claim about a third party,
   * not a gap in our own copy. Leave undefined until it is known.
   */
  makerName?: string
  makerLink?: string
  labels: ProductStatusLabel[]
  collectionNote?: string
  published: boolean
}

/**
 * Supporting photographs show the piece in the room it came out of, which
 * is most of what tells you what it is. These are the café's own interior
 * shots, so a second-hand object is shown against the real walls rather
 * than a generic styled backdrop.
 */
const SUPPORTING = {
  /**
   * The merchandise square-on: the clearest shot of the tee print, the
   * cap and the helmets. Replaced the corner crop, which showed the
   * goods at an angle and half in shadow.
   */
  merch: {
    src: IMAGES.merchandiseWall.src,
    alt: IMAGES.merchandiseWall.alt,
  },
  snug: { src: IMAGES.snugFireside.src, alt: IMAGES.snugFireside.alt },
  /** Salvage propped and hung against stone — the reclaimed pieces' world. */
  salvage: { src: IMAGES.dunlopSign.src, alt: IMAGES.dunlopSign.alt },
  engine: { src: IMAGES.engineAlcove.src, alt: IMAGES.engineAlcove.alt },
  memorabilia: {
    src: IMAGES.interiorMemorabilia.src,
    alt: IMAGES.interiorMemorabilia.alt,
  },
} as const

/** Stands in until each piece has its own photograph. */
const PENDING = {
  image: PRODUCT_PLACEHOLDER.src,
  imageAlt: PRODUCT_PLACEHOLDER.alt,
  photoPending: true,
} as const

const COLLECTION_NOTE =
  'Once we have confirmed your reservation, we will give you a ring to sort out collection or delivery, whichever suits you better.'

export const products: Product[] = [
  /**
   * The first three are the real merchandise, photographed on the rail
   * inside the café, so the shared picture genuinely shows them.
   */
  {
    id: 'LOF-0001',
    slug: 'lof-brackenfield-tee',
    name: 'Low On Fuel Café Tee',
    image: PRODUCT_PLACEHOLDER.src,
    imageAlt: PRODUCT_PLACEHOLDER.alt,
    supportingImages: [SUPPORTING.merch],
    price: 22,
    shortDescription:
      'Our own tee in navy, with the small roundel on the chest and the big Brackenfield rider print across the back.',
    stockQuantity: 18,
    oneOff: false,
    sold: false,
    makerName: 'Low On Fuel Café',
    labels: ['Low On Fuel Merchandise'],
    collectionNote: COLLECTION_NOTE,
    published: true,
  },
  {
    id: 'LOF-0002',
    slug: 'lof-trucker-cap',
    name: 'Low On Fuel Trucker Cap',
    image: PRODUCT_PLACEHOLDER.src,
    imageAlt: PRODUCT_PLACEHOLDER.alt,
    price: 16,
    shortDescription:
      'Black trucker cap with a mesh back and the café roundel on the front. Handy for helmet hair.',
    stockQuantity: 12,
    oneOff: false,
    sold: false,
    makerName: 'Low On Fuel Café',
    labels: ['Low On Fuel Merchandise'],
    collectionNote: COLLECTION_NOTE,
    supportingImages: [SUPPORTING.merch],
    published: true,
  },
  {
    id: 'LOF-0003',
    slug: 'lof-morf-snood',
    name: 'Low On Fuel Morf Snood',
    image: PRODUCT_PLACEHOLDER.src,
    imageAlt: PRODUCT_PLACEHOLDER.alt,
    price: 14,
    shortDescription:
      'A Morf Original neck tube in our own print. Wear it a dozen different ways, and you will want it on the cold mornings.',
    stockQuantity: 20,
    oneOff: false,
    sold: false,
    makerName: 'Morf Original',
    labels: ['Low On Fuel Merchandise'],
    collectionNote: COLLECTION_NOTE,
    published: true,
  },

  /**
   * Everything below is a one-off or small-batch piece that has not been
   * photographed yet, so it borrows the merchandise picture and says so.
   */
  {
    id: 'LOF-0004',
    slug: 'recycled-piston-clock',
    name: 'Recycled Piston Wall Clock',
    ...PENDING,
    supportingImages: [SUPPORTING.engine],
    price: 68,
    shortDescription:
      'A working wall clock built around a reclaimed piston and a handful of old gears. There is only one of it.',
    history:
      'Made from parts sourced locally. We are not claiming any racing history for it.',
    stockQuantity: 1,
    oneOff: true,
    sold: false,
    // Maker omitted until the real one is known. See the `makerName` note.
    labels: ['One of One', 'Handmade'],
    collectionNote: COLLECTION_NOTE,
    published: true,
  },
  {
    id: 'LOF-0005',
    slug: 'reclaimed-tank-side-table',
    name: 'Reclaimed Tank Side Table',
    ...PENDING,
    supportingImages: [SUPPORTING.snug],
    price: 145,
    shortDescription:
      'A proper characterful side table built around a reclaimed fuel tank. Genuinely one of a kind, so when it goes, it goes.',
    stockQuantity: 1,
    oneOff: true,
    sold: false,
    labels: ['One of One', 'Handmade'],
    collectionNote:
      'This one is a big old thing, so collecting it from the café is much easier. Give us a ring if you would rather we arranged delivery.',
    published: true,
  },
  {
    id: 'LOF-0006',
    slug: 'gear-wall-art',
    name: 'Reclaimed Gear Wall Art',
    ...PENDING,
    supportingImages: [SUPPORTING.salvage],
    price: 52,
    shortDescription:
      'A wall piece put together from reclaimed gears and odd small parts. Made in small batches, so there are never many about.',
    stockQuantity: 3,
    oneOff: false,
    sold: false,
    labels: ['Handmade', 'Limited Stock'],
    collectionNote: COLLECTION_NOTE,
    published: true,
  },
  {
    id: 'LOF-0007',
    slug: 'handmade-leather-keyfob',
    name: 'Handmade Leather Key Fob',
    ...PENDING,
    price: 16,
    shortDescription:
      'Hand-stitched leather key fob from a maker just up the road. Every one comes out slightly different, which is rather the point.',
    stockQuantity: 4,
    oneOff: false,
    sold: false,
    labels: ['Handmade', 'Limited Stock'],
    collectionNote: COLLECTION_NOTE,
    published: true,
  },
  /**
   * A sold item. Its page must still open and show the sold state: links
   * to it get shared, bookmarked and posted, and they keep being followed
   * after the piece has gone. A 404 at the end of one is a dead end.
   */
  {
    id: 'LOF-0008',
    slug: 'vintage-headlamp',
    name: 'Vintage Motorcycle Headlamp',
    ...PENDING,
    price: 40,
    shortDescription:
      'A cleaned-up vintage headlamp that sat on our wall for a good while. This one has found itself a new garage.',
    stockQuantity: 0,
    oneOff: true,
    sold: true,
    makerName: 'Low On Fuel Café',
    labels: ['One of One'],
    published: true,
  },
]

/** The shop grid: published and not yet sold. */
export function getShopProducts(): Product[] {
  return products.filter((product) => product.published && !product.sold)
}

export function getFeaturedProducts(): Product[] {
  return getShopProducts().slice(0, 4)
}

/** Any published product, sold ones included — so shared links resolve. */
export function getProduct(slug: string): Product | undefined {
  return products.find(
    (product) => product.slug === slug && product.published,
  )
}

export function formatPrice(price: number): string {
  return `£${price.toFixed(2)}`
}
